import { z } from 'zod';
import { RoleSlug } from '../enums/index';
import { PinPolicySchema } from '../tracking/index';

/** Device facts sent at login and on re-registration. */
export const DeviceRegistrationSchema = z.object({
  /** App-install id. Stable across launches, reset on reinstall. */
  deviceUuid: z.string().min(8).max(100),
  platform: z.enum(['android', 'ios', 'web']).default('android'),
  manufacturer: z.string().max(60).optional(),
  brand: z.string().max(60).optional(),
  model: z.string().max(80).optional(),
  osVersion: z.string().max(20).optional(),
  sdkInt: z.number().int().min(1).max(100).optional(),
  appVersion: z.string().max(20).optional(),
  pushToken: z.string().max(500).optional(),
  timezone: z.string().max(64).optional(),
  isRooted: z.boolean().default(false),
  isEmulator: z.boolean().default(false),
  /**
   * `location: 'always'` is the "Allow all the time" grant. Without it, Android
   * delivers no location once the app leaves the foreground, so the server
   * records it and the admin device-health view surfaces devices that lack it —
   * a missing grant is the single most common cause of a device that appears
   * enrolled but never reports.
   */
  permissions: z
    .object({
      location: z.enum(['always', 'while_in_use', 'denied']).default('denied'),
      notification: z.boolean().default(false),
      batteryUnrestricted: z.boolean().default(false),
    })
    .optional(),
});
export type DeviceRegistration = z.infer<typeof DeviceRegistrationSchema>;

export const LoginRequestSchema = z
  .object({
    email: z.string().email().max(190),
    password: z.string().min(1).max(200),
    device: DeviceRegistrationSchema.optional(),
    /**
     * Retries login after DEVICE_LIMIT_REACHED, retiring the previous device.
     * Always audited and the old device is force-logged-out.
     */
    forceDeviceChange: z.boolean().default(false),
  })
  .strict();
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const AuthUserSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  role: RoleSlug,
  companyId: z.number().int().nullable(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const AuthEmployeeSchema = z.object({
  id: z.number().int(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  trackingEnabled: z.boolean(),
  trackingMode: z.enum(['shift_only', 'always', 'manual', 'disabled']),
});
export type AuthEmployee = z.infer<typeof AuthEmployeeSchema>;

export const LoginResponseSchema = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  /** Seconds. */
  expiresIn: z.number().int(),
  refreshToken: z.string(),
  refreshExpiresIn: z.number().int(),
  user: AuthUserSchema,
  employee: AuthEmployeeSchema.nullable(),
  company: z.object({ id: z.number().int(), name: z.string(), timezone: z.string() }).nullable(),
  device: z.object({ id: z.number().int(), status: z.string() }).nullable(),
  /**
   * Tells the app explicitly whether to start tracking, rather than making it
   * infer the answer from trackingMode — the server owns that decision so it
   * can be changed without an app release.
   */
  tracking: z.object({
    shouldTrack: z.boolean(),
    mode: z.enum(['shift_only', 'always', 'manual', 'disabled']),
    /** Present when a session is already open (app was reinstalled mid-shift). */
    openSessionUuid: z.string().uuid().nullable(),
  }),
  /**
   * The unlock policy, so the gate is armed the moment a session exists.
   *
   * This used to be delivered only on a heartbeat, and that left a hole wide
   * enough to walk through: a freshly signed-in phone held no policy for up to
   * two minutes, and `PinStore` treats "no policy" as "open" so a fresh install
   * is not bricked shut. The app therefore let anyone straight in after login.
   * Sign-in is by definition online, so this is the earliest and cheapest place
   * to hand the policy over. The heartbeat still carries it, for the case that
   * actually needs a background channel: an admin changing the PIN later.
   *
   * Null for a user with no employee record — an office administrator on the
   * panel has nothing to unlock.
   */
  pin: PinPolicySchema.nullable(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const RefreshRequestSchema = z
  .object({
    refreshToken: z.string().min(20),
    deviceUuid: z.string().min(8).max(100).optional(),
  })
  .strict();
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;

export const RefreshResponseSchema = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number().int(),
  refreshToken: z.string(),
  refreshExpiresIn: z.number().int(),
});
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

export const MeResponseSchema = z.object({
  user: AuthUserSchema,
  employee: AuthEmployeeSchema.nullable(),
  company: z.object({ id: z.number().int(), name: z.string(), timezone: z.string() }).nullable(),
  tracking: z.object({
    shouldTrack: z.boolean(),
    mode: z.enum(['shift_only', 'always', 'manual', 'disabled']),
    openSessionUuid: z.string().uuid().nullable(),
  }),
  /** As on login — a restored session must arm the gate too. */
  pin: PinPolicySchema.nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;
